/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Switch } from '@coursera-web/cds-labs';

import { Accordion, AccordionHeader, AccordionPanel, Avatar, Divider, Tag, Typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import type { CourseEmailPreference, CourseEmailType } from 'bundles/account-settings/types/EmailPreferences';
import { COURSE_EMAIL_TYPES } from 'bundles/account-settings/types/EmailPreferences';
import { getEnabledNotificationCount } from 'bundles/account-settings/utils/notificationPreferencesHelpers';

import _t from 'i18n!nls/account-settings';

type Props = {
  course: CourseEmailPreference;
  disabled: boolean;
  onToggle: (type: CourseEmailType, checked: boolean) => void;
};

const styles = {
  accordion: css`
    margin-inline: calc(-1 * var(--cds-spacing-50));

    .cds-AccordionHeader-button {
      padding: var(--cds-spacing-50) !important;
    }

    .cds-AccordionHeader-labelGroup {
      flex: 1;

      > div {
        width: 100%;
      }
    }

    .cds-AccordionHeader-chevron {
      margin: 0 0 0 var(--cds-spacing-150);
      order: 2;
      align-self: center;
    }

    .cds-AccordionHeader-freeContent {
      display: none;
    }

    div > div {
      margin-bottom: 0;
    }
  `,
  eyebrowRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  textColumn: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  `,
  tag: css`
    display: flex;
    flex-shrink: 0;
    align-self: center;
  `,
  panel: css`
    display: flex;
    flex-direction: column;
    padding-inline: var(--cds-spacing-400) var(--cds-spacing-300);
    margin-inline-start: var(--cds-spacing-50);
  `,
  switchRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-block: var(--cds-spacing-100);

    > div {
      width: fit-content;
    }
  `,
};

const CourseAccordionRow = ({ course, disabled, onToggle }: Props) => {
  const labels: Record<CourseEmailType, string> = {
    announcements: _t('Announcements'),
    reminders: _t('Reminders'),
    discussionForums: _t('Discussion Forums'),
  };

  const enabledCount = disabled ? 0 : getEnabledNotificationCount(course.value);
  const totalCount = COURSE_EMAIL_TYPES.length;

  // Fire a view_checkbox impression for each notification type switch in this course row.
  // This may be excessive (3 events per course on mount). Will follow up with data team.
  const announcementsRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'enrolled_courses_communication_email_preference',
      label: labels.announcements,
      checked: !!course.value.announcements,
    },
    product: { type: 'course', id: course.id, name: course.name },
  });
  const remindersRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'enrolled_courses_communication_email_preference',
      label: labels.reminders,
      checked: !!course.value.reminders,
    },
    product: { type: 'course', id: course.id, name: course.name },
  });
  const discussionForumsRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'enrolled_courses_communication_email_preference',
      label: labels.discussionForums,
      checked: !!course.value.discussionForums,
    },
    product: { type: 'course', id: course.id, name: course.name },
  });

  const refs: Record<CourseEmailType, React.MutableRefObject<HTMLDivElement | null>> = {
    announcements: announcementsRef,
    reminders: remindersRef,
    discussionForums: discussionForumsRef,
  };

  return (
    <Accordion variant="silent" css={styles.accordion}>
      <AccordionHeader
        label=""
        eyebrowContent={
          <div css={styles.eyebrowRow}>
            {course.logo && <Avatar variant="organization" size={32} imgProps={{ src: course.logo, alt: '' }} />}
            <div css={styles.textColumn}>
              <Typography2 component="p" variant="subtitleMedium">
                {course.name}
              </Typography2>
              {course.description && (
                <Typography2 component="p" variant="bodySecondary" color="supportText" aria-hidden>
                  {course.description}
                </Typography2>
              )}
            </div>
            <div css={styles.tag}>
              <Tag variant="status" color={enabledCount === 0 ? 'default' : 'highlight1'} priority="secondary">
                {enabledCount === 0
                  ? _t('None selected')
                  : _t('#{selected} of #{total} selected', { selected: enabledCount, total: totalCount })}
              </Tag>
            </div>
          </div>
        }
      />
      <AccordionPanel>
        <div
          css={styles.panel}
          role="region"
          aria-label={_t('Communication from #{courseName}', { courseName: course.name })}
        >
          {COURSE_EMAIL_TYPES.map((type, index) => (
            <React.Fragment key={type}>
              {index > 0 && <Divider color="light" orientation="horizontal" />}
              <div css={styles.switchRow} ref={refs[type]}>
                <Typography2 component="span" variant="subtitleSmall">
                  {labels[type]}
                </Typography2>
                <Switch
                  aria-label={`${labels[type]} — ${course.name}`}
                  selected={!disabled && !!course.value[type]}
                  disabled={disabled}
                  onToggle={(checked: boolean) => onToggle(type, checked)}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </AccordionPanel>
    </Accordion>
  );
};

export default CourseAccordionRow;
